/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import { VehiclePartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehiclePartCategoryTreeItem';
import { ROUTER_CATALOG_DH_L3, ROUTER_CATALOG_VEHICLE, ROUTER_DETAILS, ROUTER_PRODUCT } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import PageViewCounter from 'components/Help/PageViewCounter';
import Loader from 'components/Loader';
import {
  useFetchDHTire,
  useFetchKadensisTechnicalData,
  useFetchMaintenancePlan,
  useFetchVehicle,
  useUpdateVehicleSearchHistory,
} from 'domains/catalog/Catalog.requests';
import {
  getLastSearchedVehicleKey,
  getSearchVehicleResult,
  resetPreviousTechnicalCriteria,
  setLastSearchedVehicleKey,
} from 'domains/catalog/Catalog.store';
import { FLUIDS_ID, TECHNICAL_DATA_TAB, TechnicalDataIdParam, VehicleCountry } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl, getDHCatalogSourcePaths } from 'domains/catalog/Catalog.utils';
import { useFetchIAMAvailableModules } from 'domains/catalog/CatalogIam.requests';
import { useFetchEstimateSettingsSubscription } from 'domains/estimate/Estimate.requests';
import { getDhCatalogTabsView } from 'domains/user';
import { CatalogTabs } from 'pages/CatalogPage/common';
import { ProductSection } from 'pages/CatalogPage/DH/Product/ProductSection';
import { Container, defaultPadding, Empty, indentPadding, MarginBox } from 'UI';
import { ERROR, FOUND, NOT_FOUND, SearchData } from 'utils';
import { useWindowSize } from 'utils/hooks/useWindowSize';
import CarPartGroupsSection from './CarPartGroupsSection';
import FullTextSearchResult from './FullTextSearchResult/FullTextSearchResult';
import SubcategoriesSection from './SubcategoriesSection';
import SubcategorySection from './SubcategorySection';
import Tires from './Tires';
import { VehicleTechnicalDetails } from './VehicleTechnicalDetails/VehicleTechnicalDetails';
import { getCatalogContainerSize } from '../common/catalogContainerSize';
import QuickAccess from '../common/QuickAccess';
import { useResetQuickAccessCategories } from '../common/QuickAccess/QuickAccess';

export const CatalogTabsDH = ({
  tree,
  setCatalogShowMenu,
}: {
  tree: SearchData<VehiclePartCategoryTreeItem[]> | undefined;
  setCatalogShowMenu?: (b: boolean) => void;
}) => {
  const { t } = useTranslation();
  const uparams = new URLSearchParams(location.search);
  uparams.set(TechnicalDataIdParam, FLUIDS_ID);

  return (
    <DataContainer
      data={tree}
      ErrorState={() => (
        <ErrorWithLabel
          label={t('catalog.parts.backend_error', 'Car parts are temporarily unavailable, please try again later.')}
        />
      )}
    >
      <Switch>
        <Route path={getDHCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/search/:label`)} exact>
          <FullTextSearchResult />
        </Route>
        <Route
          path={getDHCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}${ROUTER_PRODUCT}/:referenceNumber`)}
          exact
        >
          <ProductSection />
        </Route>
        <Route
          path={getDHCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId/:categoryId/:subcategoryId`)}
          exact
        >
          <div>
            <PageViewCounter page={ROUTER_CATALOG_DH_L3} />
            <SubcategorySection />
          </div>
        </Route>
        <Route path={getDHCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId/:categoryId`)} exact>
          <div>
            <SubcategoriesSection />
          </div>
        </Route>
        <Route path={getDHCatalogSourcePaths(`/:query${ROUTER_CATALOG_VEHICLE}/:carPartGroupId?`)} exact>
          <div>
            {/*todo enable when catalog l1 first help*/}
            {/*<PageViewCounter page={ROUTER_CATALOG_DH_L1} />*/}
            <CarPartGroupsSection setCatalogShowMenu={setCatalogShowMenu} />
          </div>
        </Route>
      </Switch>
    </DataContainer>
  );
};

const CatalogPage = ({ setCatalogShowMenu }: { setCatalogShowMenu: (b: boolean) => void }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { query } = useParams<{
    query: string;
  }>();
  const [isChangeVehicle, setIsChangeVehicle] = useState(false);
  const result = useSelector((state: RootState) => getSearchVehicleResult(state, query));
  const tree = result?.explodedTree;
  const searchStatus = result?.searchVehicleStatus;
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const lastSearchedResult = useSelector((state: RootState) => getSearchVehicleResult(state, lastSearchedVehicleKey));
  const params = new URLSearchParams(location.search);
  const { t } = useTranslation();
  const tabKey = useSelector(getDhCatalogTabsView);
  const { width: windowWidth } = useWindowSize();
  const catalogContainerSize = getCatalogContainerSize(windowWidth);

  useEffect(() => {
    if (lastSearchedVehicleKey !== query) {
      dispatch(resetPreviousTechnicalCriteria());
      dispatch(setLastSearchedVehicleKey(query));
    }
  }, [dispatch, query, lastSearchedVehicleKey]);

  useEffect(() => {
    if (isChangeVehicle) {
      if (
        lastSearchedVehicleKey !== query &&
        lastSearchedResult?.searchVehicleStatus === FOUND &&
        lastSearchedResult?.vehicleDetail
      ) {
        setIsChangeVehicle(false);
        history.push(
          `${getCatalogSourceUrl(lastSearchedResult?.vehicleDetail?.catalogSource)}/${
            lastSearchedResult?.vehicleDetail?.vehicleKey
          }${ROUTER_CATALOG_VEHICLE}?${params}`,
        );
      }
    }
    // eslint-disable-next-line
  }, [isChangeVehicle, lastSearchedVehicleKey]);

  useEffect(() => {
    if (tabKey !== TECHNICAL_DATA_TAB) {
      params.delete(TechnicalDataIdParam);
      history.replace(`${location.pathname}?${params}`);
    }
    // eslint-disable-next-line
  }, [tabKey]);

  useEffect(() => {
    const country = result?.vehicleDetail?.country;
    if (params.get(VehicleCountry) !== country && country) {
      params.set(VehicleCountry, country);
      history.replace(`${location.pathname}?${params}`);
    }
  });

  useFetchEstimateSettingsSubscription();
  useFetchVehicle(query, searchStatus);
  useFetchDHTire(result);
  useUpdateVehicleSearchHistory(result?.vehicleDetail);
  useFetchMaintenancePlan();
  useResetQuickAccessCategories();
  useFetchIAMAvailableModules(result?.vehicleDetail);
  useFetchKadensisTechnicalData();

  if (!result || !result.vehicleDetail) {
    switch (searchStatus) {
      case NOT_FOUND:
        return (
          <Container padding={defaultPadding} size={catalogContainerSize}>
            <MarginBox my={220}>
              <Empty />
            </MarginBox>
          </Container>
        );
      case ERROR:
        return (
          <Container padding={defaultPadding} size={catalogContainerSize}>
            <MarginBox my={220}>
              <ErrorWithLabel
                label={t(
                  'catalog.parts.search.by_vin_or_vrn.error',
                  'Sorry, we could not find any vehicle associated, please try again later.',
                )}
              />
            </MarginBox>
          </Container>
        );
      default:
        return <Loader />;
    }
  }

  const catalogTabs = (
    <CatalogTabs
      result={result.vehicleDetail}
      vehicleImage={result?.vehicleDetail?.imageUrl}
      query={query}
      setCatalogShowMenu={() => undefined}
      setIsChangeVehicle={setIsChangeVehicle}
      vehicleRouteChildren={
        <>
          <QuickAccess />
          <CatalogTabsDH tree={tree} setCatalogShowMenu={setCatalogShowMenu} />
        </>
      }
      tiresRouteChildren={
        <>
          <QuickAccess />
          <Tires setCatalogShowMenu={setCatalogShowMenu} />
        </>
      }
      catalogPage={'RENAULT_CATALOG'}
    />
  );

  return (
    <Container
      padding={indentPadding}
      size={catalogContainerSize}
      alignText={'left'}
      justify={'flex-start'}
      data-cy={'dh-catalog-page'}
    >
      {catalogTabs}
      <Switch>
        <Route path={getDHCatalogSourcePaths(`/:query${ROUTER_DETAILS}`)} exact>
          <VehicleTechnicalDetails />
        </Route>
      </Switch>
    </Container>
  );
};

export default CatalogPage;
